import * as fun from 'chatgpt-funcaptcha'
import delay from 'delay'
import ExpiryMap from 'expiry-map'
import pTimeout from 'p-timeout'
import type { Browser, Page } from 'puppeteer'
import { v4 as uuidv4 } from 'uuid'

import * as types from './types'
import { AChatGPTAPI } from './abstract-chatgpt-api'
import { getProofToken, getRequirementsToken } from './get-proof-token'
import {
  getBrowser,
  getOpenAIAuth,
  getPage,
  solveSimpleCaptchas
} from './openai-auth'
import {
  browserPostEventStream,
  getGptMessages,
  getRequirementToken,
  getWssUrl,
  markdownToText,
  maximizePage,
  minimizePage
} from './utils'

const CHAT_PAGE_URL = 'https://chatgpt.com/'

export class ChatGPTAPI extends AChatGPTAPI {
  getIsAuthenticated(): Promise<boolean> {
    throw new Error('Method not implemented.')
  }
  refreshSession(): Promise<any> {
    throw new Error('Method not implemented.')
  }
  protected _markdown: boolean
  protected _debug: boolean
  protected _apiBaseUrl: string
  protected _backendApiBaseUrl: string
  protected _headers: Record<string, string>
  protected _browser: Browser
  protected _page: Page
  protected _minimize: boolean
  protected _capsolverKey: string
  protected _nopechaKey: string

  protected _funObj: {
    pkey: string
    bda: string
    aev: string
    userAgent: string
    surl: string
  }
  protected _messageOnProgressHandlers: Record<
    string,
    (partialResponse: types.ChatResponse) => void
  >
  /**
   * Creates a new client wrapper around the unofficial ChatGPT REST API.
   *
   * Note that your IP address and `userAgent` must match the same values that you used
   * to obtain your `clearanceToken`.
   * @param apiBaseUrl - Optional override; the base URL for ChatGPT webapp's API (`/api`)
   * @param backendApiBaseUrl - Optional override; the base URL for the ChatGPT backend API (`/backend-api`)
   * @param heaaders - Optional additional HTTP headers to be added to each `fetch` request
   * @param debug - Optional enables logging debugging into to stdout
   */
  constructor(opts: {
    /** @defaultValue `true` **/
    minimize?: boolean

    /** @defaultValue `true` **/
    markdown?: boolean

    /** @defaultValue `'https://chat.openai.com/api'` **/
    apiBaseUrl?: string

    /** @defaultValue `'https://chat.openai.com/backend-api'` **/
    backendApiBaseUrl?: string

    /** @defaultValue `false` **/
    debug?: boolean

    /** @defaultValue `capsolverKey` **/
    capsolverKey?: string

    nopechaKey?: string
  }) {
    super()

    const {
      minimize = true,
      markdown = true,
      apiBaseUrl = 'https://chatgpt.com/api',
      backendApiBaseUrl = 'https://chatgpt.com/backend-api',
      debug = false,
      capsolverKey = process.env.CAPSOLVER_KEY,
      nopechaKey = process.env.NOPECHA_KEY
    } = opts
    this._capsolverKey = capsolverKey
    this._nopechaKey = nopechaKey
    this._minimize = !!minimize
    this._markdown = !!markdown
    this._debug = !!debug
    this._apiBaseUrl = apiBaseUrl
    this._backendApiBaseUrl = backendApiBaseUrl
    this._messageOnProgressHandlers = {}
  }

  /**
   * Refreshes the client's access token which will succeed only if the session
   * is valid.
   */
  override async initSession() {
    try {
      this._browser = await getBrowser({
        minimize: this._minimize,
        nopechaKey: this._nopechaKey,
        capsolverKey: this._capsolverKey
      })
      this._page = await getPage(this._browser, {})
      await this._page.goto(CHAT_PAGE_URL, {
        waitUntil: 'networkidle2'
      })
      console.log(this._page.url(), 'chatgptAPI')
      if (this._page.url() !== CHAT_PAGE_URL) {
        await getOpenAIAuth({
          email: 'getjoonai@gmail.com',
          password: '$123321@Hh#_',
          browser: this._browser,
          page: this._page
        })
      }
      await solveSimpleCaptchas(this._page)
      this._funObj = fun.readHar()
    } catch (error) {
      if (this._browser) {
        await this._browser.close()
      }

      this._browser = null
      this._page = null
      throw error
    }
    await delay(20000)

    await this._page.exposeFunction(
      'ChatGPTAPIBrowserOnProgress',
      (partialResponse: types.ChatResponse) => {
        if ((partialResponse as any)?.origMessageId) {
          const onProgress =
            this._messageOnProgressHandlers[
              (partialResponse as any).origMessageId
            ]

          if (onProgress) {
            onProgress(partialResponse)
            return
          }
        }
      }
    )
    if (this._minimize) {
      return minimizePage(this._page)
    }
  }

  /**
   * Sends a message to ChatGPT, waits for the response to resolve, and returns
   * the response.
   *
   * If you want to receive a stream of partial responses, use `opts.onProgress`.
   * If you want to receive the full response, including message and conversation IDs,
   * you can use `opts.onConversationResponse` or use the `ChatGPTAPI.getConversation`
   * helper.
   *
   * @param message - The prompt message to send
   * @param opts.conversationId - Optional ID of a conversation to continue
   * @param opts.parentMessageId - Optional ID of the previous message in the conversation
   * @param opts.messageId - Optional ID of the message to send (defaults to a random UUID)
   * @param opts.action - Optional ChatGPT `action` (either `next` or `variant`)
   * @param opts.timeoutMs - Optional timeout in milliseconds (defaults to no timeout)
   * @param opts.onProgress - Optional callback which will be invoked every time the partial response is updated
   * @param opts.abortSignal - Optional callback used to abort the underlying `fetch` call using an [AbortController](https://developer.mozilla.org/en-US/docs/Web/API/AbortController)
   *
   * @returns The response from ChatGPT
   */
  override async sendMessage(
    messages: types.SendMessages,
    opts: types.SendMessageOptions
  ): Promise<types.ChatResponse> {
    const { action = 'next', accessToken, timeoutMs, onProgress } = opts
    const userAgent = await this._browser.userAgent()
    const scripts = await this._page.evaluate(() => {
      const scriptElements = Array.from(
        document.querySelectorAll('script[src]')
      )
      return scriptElements.map((script) => script.getAttribute('src'))
    })
    const requirement = await this._page.evaluate(
      getRequirementToken,
      getRequirementsToken(userAgent, scripts),
      accessToken
    )

    let arkoseToken = null
    if (requirement.arkose?.required && this._funObj) {
      const tokenObj = await fun.getToken({
        pkey: this._funObj.pkey,
        surl: this._funObj.surl,
        bda: this._funObj.bda,
        headers: {
          'User-Agent': userAgent
        },
        data: {
          blob: requirement.arkose.dx
        },
        site: 'https://chatgpt.com'
      })
      arkoseToken = tokenObj.token
    }

    let bodyMessages = []
    for (let index = 0; index < messages.messages.length; index++) {
      const element = messages.messages[index]
      const gptMessage = await this._page.evaluate(
        getGptMessages,
        element.content,
        accessToken
      )
      const bodyMessage = {
        id: uuidv4(),
        author: {
          role: element.role === 'system' ? 'critic' : element.role
        },
        content: {
          content_type: gptMessage.attachments.length
            ? 'multimodal_text'
            : 'text',
          parts: gptMessage.parts
        },
        metadata: {
          attachments: gptMessage.attachments
        }
      }

      bodyMessages.push(bodyMessage)
    }

    const body: types.ConversationJSONBody = {
      action,
      messages: bodyMessages,
      model:
        messages.model.indexOf('gpt-4o') !== -1
          ? 'gpt-4o'
          : messages.model.indexOf('gpt-4') !== -1
          ? 'gpt-4'
          : 'text-davinci-002-render-sha',
      parent_message_id: uuidv4(),
      timezone_offset_min: -480,
      websocket_request_id: uuidv4(),
      suggestions: [],
      history_and_training_disabled: true,
      conversation_mode: {
        kind: 'primary_assistant'
      },
      force_paragen: false,
      force_paragen_model_slug: '',
      force_nulligen: false,
      force_rate_limit: false,
      reset_rate_limits: false,
      force_use_sse: true
      // variant_purpose: 'comparison_implicit'
    }

    const url = `https://chatgpt.com/backend-anon/conversation`

    const last_id = bodyMessages[bodyMessages.length - 1].id
    if (onProgress) {
      this._messageOnProgressHandlers[last_id] = onProgress
    }

    const cleanup = () => {
      if (this._messageOnProgressHandlers[last_id]) {
        delete this._messageOnProgressHandlers[last_id]
      }
    }

    let wssUrl: string
    if (!body.force_use_sse) {
      wssUrl = await this._page.evaluate(getWssUrl, accessToken)
    }

    let powToken: string
    if (requirement?.proofofwork?.required) {
      powToken = getProofToken({
        required: requirement.proofofwork.required,
        seed: requirement.proofofwork.seed,
        difficulty: requirement.proofofwork.difficulty,
        userAgent: userAgent,
        scriptArr: scripts
      })
    }

    let result: types.ChatResponse | types.ChatError
    let numTries = 0
    let is401 = false

    do {
      try {
        result = await this._page.evaluate(
          browserPostEventStream,
          url,
          accessToken,
          body,
          requirement,
          powToken,
          wssUrl,
          arkoseToken,
          timeoutMs
        )
      } catch (err) {
        // We catch all errors in `browserPostEventStream`, so this should really
        // only happen if the page is refreshed or closed during its invocation.
        // This may happen if we encounter a 401/403 and refresh the page in it's
        // response handler or if the user has closed the page manually.

        if (++numTries >= 2) {
          const error = new types.ChatGPTError(err.toString(), { cause: err })
          error.statusCode = err.response?.statusCode
          error.statusText = err.response?.statusText
          cleanup()
          throw error
        }

        console.warn('chatgpt sendMessage error; retrying...', err.toString())
        await delay(5000)
        continue
      }

      if ('error' in result) {
        const error = new types.ChatGPTError(result.error.message)
        error.statusCode = result.error.statusCode
        error.statusText = result.error.statusText

        ++numTries

        if (error.statusCode === 401) {
          is401 = true

          if (numTries >= 2) {
            cleanup()
            throw error
          } else {
            continue
          }
        } else if (error.statusCode !== 403) {
          throw error
        } else {
          result = null
          continue
        }
      } else {
        if (!this._markdown) {
          result.response = markdownToText(result.response)
        }

        cleanup()
        return result
      }
    } while (!result)

    cleanup()
  }
  override async closeSession() {}
}
